import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square, Upload, Sparkles, ClipboardCopy, AlertCircle, RefreshCw, X } from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';

// RADIOLOGY DICTATION (Phase 4, #15 — radiology pack). The radiologist speaks;
// the whisper pipeline transcribes and the LLM shapes a DRAFT (technique /
// findings / impression). Each section has a copy button so the radiologist
// pastes what they accept into the real report form — the draft never writes
// into the report by itself, and signing stays the human act it is.

const POLL_MS = 3000;
const POLL_LIMIT = 100; // dictations transcribe slower than images OCR

const DictationPanel = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const fileRef = useRef(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!open) {
      setJob(null); setError('');
      if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
      setRecording(false);
    }
  }, [open]);

  useEffect(() => {
    if (!job || ['done', 'failed'].includes(job.status)) return undefined;
    const id = setInterval(async () => {
      pollCount.current += 1;
      if (pollCount.current > POLL_LIMIT) {
        clearInterval(id);
        setError(t('aiPacks.timeout', 'This is taking longer than expected — try again.'));
        return;
      }
      try {
        const axiosInstance = createAuthedAxios();
        const { data } = await axiosInstance.get(`${baseURL}/ai-packs/jobs/${job.jobId}`);
        setJob(data.job);
      } catch { /* transient — keep polling */ }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [job, t]);

  const submitAudio = async (blobOrFile, filename) => {
    setBusy(true); setError(''); setJob(null); pollCount.current = 0;
    try {
      const axiosInstance = createAuthedAxios();
      const form = new FormData();
      form.append('audio', blobOrFile, filename);
      const { data } = await axiosInstance.post(`${baseURL}/ai-packs/radiology/dictation`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setJob(data.job);
    } catch (err) {
      const msg = err?.response?.data?.message || t('aiPacks.dictation_failed', 'Could not send the dictation');
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        submitAudio(blob, 'dictation.webm');
      };
      mediaRef.current = recorder;
      recorder.start();
      setRecording(true);
    } catch {
      setError(t('aiPacks.mic_denied', 'Microphone access was refused — upload an audio file instead.'));
    }
  };

  const stopRecording = () => {
    setRecording(false);
    if (mediaRef.current?.state === 'recording') mediaRef.current.stop();
  };

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text || '');
    toast.info(t('aiPacks.copied', { label, defaultValue: `${label} copied` }));
  };

  if (!open) return null;
  const draft = job?.status === 'done' ? job.result : null;

  const Section = ({ label, text }) => (
    <div className="border border-gray-100 rounded-xl p-3.5">
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>
        <button onClick={() => copy(text, label)} className="p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-[#2D8A8A] transition-colors cursor-pointer" title={t('aiPacks.copy', 'Copy')}>
          <ClipboardCopy size={14} />
        </button>
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-wrap">{text || '—'}</p>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="dictation-panel">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full flex flex-col max-h-[85vh]">
        <div className="border-b border-gray-100 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center">
              <Sparkles size={17} className="text-white" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">{t('aiPacks.dictation_title', 'Dictate to draft')}</h2>
              <p className="text-[11px] text-gray-400">{t('aiPacks.dictation_sub', 'Speak the findings — paste what you accept into the report. You sign; it never does.')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors"><X size={20} /></button>
        </div>

        <div className="p-4 overflow-y-auto space-y-3">
          {!job && (
            <div className="flex flex-col items-center gap-3 py-6">
              <button
                onClick={recording ? stopRecording : startRecording}
                disabled={busy}
                className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all cursor-pointer disabled:opacity-50 ${recording ? 'bg-rose-500 text-white animate-pulse' : 'bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] text-white hover:shadow-xl'}`}
                data-testid="dictation-record"
              >
                {recording ? <Square size={22} /> : <Mic size={24} />}
              </button>
              <p className="text-sm text-gray-500">
                {recording ? t('aiPacks.recording', 'Recording — press to finish') : t('aiPacks.press_to_dictate', 'Press and dictate')}
              </p>
              <button onClick={() => fileRef.current?.click()} disabled={busy || recording}
                className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-[#2D8A8A] transition-colors cursor-pointer disabled:opacity-50">
                <Upload size={13} /> {t('aiPacks.or_upload_audio', 'or upload an audio file')}
              </button>
              <input ref={fileRef} type="file" accept="audio/*" className="hidden"
                onChange={(e) => e.target.files?.[0] && submitAudio(e.target.files[0], e.target.files[0].name)} />
            </div>
          )}

          {(busy || (job && !['done', 'failed'].includes(job.status))) && (
            <div className="py-10 flex flex-col items-center gap-3">
              <span className="border-4 border-[#4ABDBD] border-t-transparent rounded-full w-10 h-10 animate-spin" />
              <p className="text-sm text-gray-500">
                {busy ? t('aiPacks.uploading', 'Uploading…')
                  : job?.status === 'queued' ? t('aiPacks.transcribing', 'Transcribing the dictation…')
                    : t('aiPacks.drafting', 'Shaping the draft…')}
              </p>
            </div>
          )}

          {job?.status === 'failed' && (
            <div className="py-8 text-center">
              <AlertCircle size={32} className="mx-auto text-rose-400 mb-2" />
              <p className="text-sm text-gray-600">{t('aiPacks.dictation_job_failed', 'Could not process this dictation.')}</p>
              <button onClick={() => { setJob(null); setError(''); }}
                className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#2D8A8A] hover:underline cursor-pointer">
                <RefreshCw size={14} /> {t('aiPacks.try_again', 'Try again')}
              </button>
            </div>
          )}

          {draft && (
            <>
              {draft.technique && <Section label={t('aiPacks.technique', 'Technique')} text={draft.technique} />}
              <Section label={t('aiPacks.findings', 'Findings')} text={draft.findings} />
              <Section label={t('aiPacks.impression', 'Impression')} text={draft.impression} />
              <p className="text-[11px] text-amber-600 flex items-start gap-1.5">
                <AlertCircle size={13} className="mt-0.5 shrink-0" />
                {t('aiPacks.dictation_disclaimer', 'A draft from your own words — review, edit, and sign in the report form.')}
              </p>
            </>
          )}

          {error && <p className="text-sm text-rose-500">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default DictationPanel;
